import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Popover,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import { styled, alpha, useTheme, type Theme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { AddLocationModal } from './AddLocationModal';
import { EditLocationModal } from './EditLocationModal';
import { ImportLocationModal } from './ImportLocationModal';
import { LocationMapModal } from './LocationMapModal';
import { RestoreLocationsModal } from './RestoreLocationsModal';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef } from '@mui/x-data-grid';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { type GeoJSONPolygon } from 'utils/geohash';
import LocationImporterAPI from 'api/location-importer.api';

export interface LocationRecord {
  id: string;
  name: string;
  locationType: string;
  easting: number;
  northing: number;
  elevation: number;
  active: boolean;
  centroidLat?: number;
  centroidLon?: number;
  trucksCapacity?: number;
  minutes?: number;
  geohashes?: string[];
  zoneGeog?: GeoJSONPolygon | null;
}

const Ring = styled(Box, {
  shouldForwardProp: (p) => p !== 'delay' && p !== 'sz',
})<{ delay: number; sz: number }>(({ theme, delay, sz }) => ({
  position: 'absolute',
  width: sz,
  height: sz,
  borderRadius: '50%',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  '@keyframes radar': {
    '0%': { transform: 'scale(0.8)', opacity: 0.5 },
    '80%': { opacity: 0.08 },
    '100%': { transform: 'scale(2.6)', opacity: 0 },
  },
  animation: 'radar 3s ease-out infinite',
  animationDelay: `${delay}s`,
  pointerEvents: 'none',
}));

const fmtCoord = ({ value }: { value: unknown }) => {
  const n = Number(value);
  return !isNaN(n) ? n.toFixed(2) : '—';
};

const API_BASE = `${process.env.REACT_APP_MANAGEMENT_API_URL ?? ''}/api`;
const PAGE_SIZE = 25;

const LocationImporterContent = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accessToken, customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;
  const locationApi = useRef(new LocationImporterAPI()).current;

  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<LocationRecord | null>(null);
  const [mapLocation, setMapLocation] = useState<LocationRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [softDelete, setSoftDelete] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>({
    name: true,
    locationType: true,
    active: true,
    easting: true,
    northing: true,
    elevation: true,
    trucksCapacity: false,
    minutes: false,
  });

  const fetchLocations = useCallback(async () => {
    if (!accessToken || !customerId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/location-importer?customerId=${customerId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!res.ok) throw new Error(`Failed to fetch locations: ${res.status}`);
      const data: Array<Record<string, unknown>> = await res.json();
      setLocations(
        data.map((r) => ({
          id: r.locationId as string,
          name: r.name as string,
          locationType: r.locationType as string,
          easting: r.easting as number,
          northing: r.northing as number,
          elevation: r.elevation as number,
          active: r.active as boolean,
          ...(r.centroidLat != null && {
            centroidLat: r.centroidLat as number,
          }),
          ...(r.centroidLon != null && {
            centroidLon: r.centroidLon as number,
          }),
          ...(r.trucksCapacity != null && {
            trucksCapacity: r.trucksCapacity as number,
          }),
          ...(r.minutes != null && {
            minutes: r.minutes as number,
          }),
          ...((r.geohashes as string[] | undefined)?.length && {
            geohashes: r.geohashes as string[],
          }),
          zoneGeog: (r.zoneGeog as GeoJSONPolygon | null | undefined) ?? null,
        }))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [accessToken, customerId]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  interface BulkSaveResult {
    message: string;
    created: number;
    failed: number;
    failedLocations: Array<Record<string, unknown> & { reason: string }>;
  }

  const bulkSave = async (
    locs: Array<Omit<LocationRecord, 'id'>>
  ): Promise<BulkSaveResult> => {
    const res = await fetch(`${API_BASE}/location-importer/bulk`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        locations: locs.map((l) => ({ ...l, customerId })),
      }),
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return await res.json();
  };

  const handleAdd = async (loc: LocationRecord) => {
    try {
      const { id: _id, ...payload } = loc;
      const result = await bulkSave([payload]);
      if (result.failed > 0) {
        toast(
          result.failedLocations[0]?.reason ??
            t('loc_importer_toast_add_failed'),
          'error'
        );
        return;
      }
      await fetchLocations();
      setAddOpen(false);
      toast(t('loc_importer_toast_added'), 'success');
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('loc_importer_toast_add_failed'),
        'error'
      );
    }
  };

  const handleImport = async (locs: LocationRecord[]) => {
    try {
      const payloads = locs.map(({ id: _id, ...rest }) => rest);
      const result = await bulkSave(payloads);
      await fetchLocations();
      setImportOpen(false);
      if (result.failed > 0) {
        toast(
          t('loc_importer_toast_import_partial', {
            created: result.created,
            failed: result.failed,
            reason: result.failedLocations[0]?.reason ?? '',
          }),
          'error'
        );
      } else {
        toast(
          t('loc_importer_toast_imported', { count: locs.length }),
          'success'
        );
      }
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('loc_importer_toast_import_failed'),
        'error'
      );
    }
  };

  const handleUpdate = async (updated: Omit<LocationRecord, 'id'>) => {
    if (!editLocation) return;
    try {
      const res = await fetch(
        `${API_BASE}/location-importer/${editLocation.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updated),
        }
      );
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      await fetchLocations();
      setEditLocation(null);
      toast(t('loc_importer_toast_updated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('loc_importer_toast_update_failed'),
        'error'
      );
    }
  };

  const handleDelete = useCallback((id: string, name: string) => {
    const target: { id: string; name: string } = { id, name };
    setDeleteTarget(target);
    setDeleteConfirmText('');
    setSoftDelete(true);
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await locationApi.deleteLocation(deleteTarget.id, softDelete);
      await fetchLocations();
      toast(
        t('loc_importer_toast_deleted', { name: deleteTarget.name }),
        'success'
      );
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('loc_importer_toast_delete_failed'),
        'error'
      );
    } finally {
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  };

  const hasData = locations.length > 0;

  const filteredLocations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) => {
      return (
        String(l.name ?? '')
          .toLowerCase()
          .includes(q) ||
        String(l.locationType ?? '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [locations, searchQuery]);

  // reset to page 0 whenever search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredLocations.length / PAGE_SIZE)
  );
  const safePage = Math.min(page, pageCount - 1);
  const pagedLocations = filteredLocations.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );
  const hasResults = filteredLocations.length > 0;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('loc_importer_col_name'),
        flex: 1.5,
        minWidth: 140,
      },
      {
        field: 'locationType',
        headerName: t('loc_importer_col_type'),
        flex: 1,
        minWidth: 110,
      },
      {
        field: 'active',
        headerName: t('loc_importer_col_active'),
        flex: 0.7,
        minWidth: 90,
        renderCell: (params) => (
          <Chip
            label={
              params.value
                ? t('loc_importer_chip_yes')
                : t('loc_importer_chip_no')
            }
            size="small"
            color={params.value ? 'success' : 'default'}
            sx={{ fontSize: '0.7rem' }}
          />
        ),
      },
      {
        field: 'easting',
        headerName: t('loc_importer_col_easting'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'northing',
        headerName: t('loc_importer_col_northing'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'elevation',
        headerName: t('loc_importer_col_elevation'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'trucksCapacity',
        headerName: t('loc_importer_col_trucksCapacity'),
        flex: 1,
        minWidth: 120,
        type: 'number',
      },
      {
        field: 'minutes',
        headerName: t('loc_importer_col_minutes'),
        flex: 1,
        minWidth: 120,
        type: 'number',
      },
      {
        field: 'actions',
        headerName: t('loc_importer_col_actions'),
        width: 140,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Tooltip title={t('loc_importer_map')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMapLocation(params.row as LocationRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <MapOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('loc_importer_edit')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditLocation(params.row as LocationRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('loc_importer_delete')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(
                    params.row.id as string,
                    params.row.name as string
                  );
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleDelete, setEditLocation, setMapLocation, t]
  );

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        px: '32px',
        pt: '20px',
        pb: '20px',
        mx: -4,
        height: 'calc(100vh - 130px)',
        minHeight: 500,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        {/* Left: title + subtitle + stat pills */}
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            lineHeight={1.2}
          >
            {t('loc_importer_title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.4, mb: 1.5 }}
          >
            {t('loc_importer_subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.4,
                py: 0.35,
                borderRadius: '6px',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.22),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {locations.length} {t('loc_importer_total')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: action buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            mt: 0.5,
            alignItems: 'center',
          }}
        >
          <Tooltip title={t('loc_importer_refresh')}>
            <IconButton
              onClick={() => {
                void fetchLocations();
              }}
              disabled={loading}
              sx={{
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.divider, 0.5),
                borderRadius: '8px',
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.4s',
                  transform: loading ? 'rotate(180deg)' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RestoreFromTrashOutlinedIcon />}
            onClick={() => {
              setRestoreOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('loc_importer_restore_btn')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('loc_importer_add_btn')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              setImportOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('loc_importer_import_btn')}
          </Button>
        </Box>
      </Box>

      {/* ── BODY ── */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!hasData ? (
          /* ── Empty state ── */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              /* dot grid */
              backgroundImage: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 1px, transparent 1px)`,
              backgroundSize: '28px 28px',
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.divider, 0.5),
            }}
          >
            {/* vignette to fade grid at edges */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: (t) =>
                  `radial-gradient(ellipse 65% 65% at 50% 50%, transparent 25%, ${t.palette.background.default} 100%)`,
                pointerEvents: 'none',
              }}
            />

            {/* Radar icon */}
            <Box
              sx={{
                position: 'relative',
                width: 96,
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3.5,
                zIndex: 1,
              }}
            >
              <Ring delay={0} sz={96} />
              <Ring delay={1} sz={96} />
              <Ring delay={2} sz={96} />
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  border: '2px solid',
                  borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                  boxShadow: `0 0 40px ${alpha(
                    theme.palette.primary.main,
                    0.25
                  )}`,
                  zIndex: 1,
                  position: 'relative',
                }}
              >
                <FmdGoodOutlinedIcon
                  sx={{ fontSize: 40, color: 'primary.main' }}
                />
              </Box>
            </Box>

            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              textAlign="center"
              sx={{ zIndex: 1 }}
            >
              {t('loc_importer_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1, mb: 4, maxWidth: 360, lineHeight: 1.8, zIndex: 1 }}
            >
              {t('loc_importer_empty_desc')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, zIndex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('loc_importer_add_btn')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setImportOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('loc_importer_import_btn')}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Data table ── */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                {t('loc_importer_all_locations')}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="primary.main"
                  fontWeight={700}
                >
                  (
                  {searchQuery.trim()
                    ? `${filteredLocations.length} / ${locations.length}`
                    : locations.length}
                  )
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small"
                  placeholder={t('loc_importer_search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                          }}
                          edge="end"
                          sx={{ p: 0.25 }}
                        >
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: 220,
                    mr: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.82rem',
                    },
                  }}
                />
                <Button
                  size="small"
                  startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                  onClick={(e) => {
                    setColAnchor(e.currentTarget);
                  }}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.76rem',
                    borderRadius: '6px',
                  }}
                >
                  {t('loc_importer_filters')}
                </Button>
                <Popover
                  open={Boolean(colAnchor)}
                  anchorEl={colAnchor}
                  onClose={() => {
                    setColAnchor(null);
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: (th: Theme) =>
                        alpha(th.palette.divider, 0.5),
                      p: 1.5,
                      minWidth: 200,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      px: 0.5,
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    {t('loc_importer_col_visibility')}
                  </Typography>
                  {Object.keys(colVisibility).map((col) => (
                    <Box key={col} sx={{ display: 'block' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={colVisibility[col]}
                            onChange={(e) => {
                              setColVisibility((prev) => ({
                                ...prev,
                                [col]: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.82rem' }}
                          >
                            {t(
                              `loc_importer_col_${
                                col === 'locationType' ? 'type' : col
                              }`
                            )}
                          </Typography>
                        }
                        sx={{ mx: 0, width: '100%' }}
                      />
                    </Box>
                  ))}
                </Popover>
              </Box>
            </Box>
            {hasResults ? (
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                <CustomStyledDataGrid
                  rows={pagedLocations}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  hideFooter
                  columnVisibilityModel={colVisibility}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('loc_importer_no_results')}
                </Typography>
              </Box>
            )}

            {hasResults && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: (th: Theme) => alpha(th.palette.divider, 0.4),
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setPage((p) => Math.max(0, p - 1));
                  }}
                  disabled={safePage === 0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 0.5,
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 80, textAlign: 'center' }}
                >
                  {safePage + 1} / {pageCount}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setPage((p) => Math.min(pageCount - 1, p + 1));
                  }}
                  disabled={safePage >= pageCount - 1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 0.5,
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.background.default, 0.6),
            zIndex: 10,
          }}
        >
          <CircularProgress size={36} />
        </Box>
      )}

      <AddLocationModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={(loc) => {
          void handleAdd(loc);
        }}
        locations={locations}
      />
      <EditLocationModal
        open={editLocation !== null}
        location={editLocation}
        onClose={() => {
          setEditLocation(null);
        }}
        onSave={(updated) => {
          void handleUpdate(updated);
        }}
        locations={locations}
      />
      <ImportLocationModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImport={async (locs) => {
          await handleImport(locs);
        }}
      />

      <LocationMapModal
        open={mapLocation !== null}
        location={mapLocation}
        onClose={() => {
          setMapLocation(null);
        }}
      />

      <RestoreLocationsModal
        open={restoreOpen}
        onClose={() => {
          setRestoreOpen(false);
        }}
        onRestored={() => {
          void fetchLocations();
        }}
      />

      {/* ── Delete Confirmation ── */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmText('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.1),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {t('loc_importer_delete_confirm_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('loc_importer_delete_confirm_subtitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mb: 2,
              borderRadius: 2,
              bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.06),
              border: '1px solid',
              borderColor: (th: Theme) => alpha(th.palette.error.main, 0.2),
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color="error.main"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {deleteTarget?.name}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('loc_importer_delete_confirm_instruction')}
          </Typography>

          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={deleteTarget?.name ?? ''}
            value={deleteConfirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDeleteConfirmText(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (
                e.key === 'Enter' &&
                deleteConfirmText === deleteTarget?.name
              ) {
                void confirmDelete();
              }
            }}
            error={
              deleteConfirmText.length > 0 &&
              deleteConfirmText !== deleteTarget?.name
            }
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace' } }}
          />

          <FormControlLabel
            sx={{ mt: 1.5, ml: 0 }}
            control={
              <Checkbox
                checked={softDelete}
                onChange={(e) => {
                  setSoftDelete(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t('loc_importer_delete_soft_checkbox')}
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteConfirmText('');
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('loc_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deleteTarget?.name}
            onClick={() => {
              void confirmDelete();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('loc_importer_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const LocationImporterView = (): JSX.Element => {
  return (
    <CustomMuiThemeProvider>
      <LocationImporterContent />
    </CustomMuiThemeProvider>
  );
};
